import { ref, readonly } from "vue";
import type * as msal from "@azure/msal-browser";
import {
  loadProfileWithCache,
  AuthError,
} from "@/features/profile/services/graph-api";
import {
  getCachedPhoto,
  cachePhotoOnly,
  clearGraphCache,
} from "@/features/profile/services/graph-cache";
import { mapGraphProfileToUserData } from "@/features/profile/services/graph-user-mapper";
import { fileToBase64 } from "@/shared/services/file-utils";
import { userDataCache } from "@/shared/services/user-data-cache";
import { flushRoaming } from "@/shared/services/storage";
import type { GraphUserProfile } from "@/shared/types";
import { useAuth } from "@/features/auth/composables/useAuth";

const profile = ref<GraphUserProfile | null>(null);
const photo = ref<string | null>(null);
const microsoftPhoto = ref<string | null>(null);
const photoChangedLocally = ref(false);
const isLoading = ref(false);

/**
 * Composable de integração com Microsoft Graph para carregar perfil e foto do usuário.
 *
 * @returns Objeto reativo com dados do perfil, foto e métodos de manipulação.
 *
 * @example
 * ```typescript
 * const { profile, photo, loadProfile, uploadLocalPhoto } = useGraph();
 * await loadProfile();
 * ```
 */
export function useGraph() {
  const { getMsalInstance } = useAuth();

  const graphError = ref<string | null>(null);
  const graphAuthExpired = ref(false);

  /**
   * Carrega o perfil e a foto do usuário a partir do Microsoft Graph com suporte a cache.
   *
   * @param force - Se `true`, ignora o cache válido e busca sempre do Graph.
   * @returns Promise que resolve quando o carregamento é concluído.
   *
   * @example
   * ```typescript
   * const { loadProfile } = useGraph();
   * await loadProfile(true); // sempre busca do Graph ao abrir o painel
   * ```
   */
  async function loadProfile(force: boolean = false): Promise<void> {
    isLoading.value = true;
    graphError.value = null;
    graphAuthExpired.value = false;
    try {
      const msal = getMsalInstance();
      const result = await loadProfileWithCache(msal, force);

      profile.value = result.profile;
      if (result.photo) {
        photo.value = result.photo;
        microsoftPhoto.value = result.photo;
        photoChangedLocally.value = false;
      } else {
        const cached = getCachedPhoto();
        if (cached) {
          photo.value = cached;
          microsoftPhoto.value = cached;
        }
      }
    } catch (err: any) {
      console.warn("[useGraph] Falha ao carregar perfil do Graph:", err);
      graphError.value = err?.message || "Erro ao acessar Microsoft Graph.";

      const isAuthErr = err instanceof AuthError || err?.name === "AuthError";
      if (isAuthErr && err?.interactionRequired === true) {
        graphAuthExpired.value = true;
      }
      const cached = getCachedPhoto();
      if (cached) {
        photo.value = cached;
        microsoftPhoto.value = cached;
      }
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Converte e armazena uma foto local como substituta da foto do Microsoft Graph.
   *
   * @param file - Arquivo de imagem selecionado pelo usuário.
   * @returns Promise que resolve quando a foto é convertida e armazenada em cache.
   *
   * @example
   * ```typescript
   * const { uploadLocalPhoto } = useGraph();
   * await uploadLocalPhoto(selectedFile);
   * ```
   */
  async function uploadLocalPhoto(file: File): Promise<void> {
    const base64 = await fileToBase64(file);
    photo.value = base64;
    photoChangedLocally.value = true;
    cachePhotoOnly(base64);
  }

  /**
   * Restaura a foto original do Microsoft Graph, descartando alterações locais.
   *
   * @example
   * ```typescript
   * const { restoreMicrosoftPhoto } = useGraph();
   * restoreMicrosoftPhoto();
   * ```
   */
  function restoreMicrosoftPhoto(): void {
    if (microsoftPhoto.value) {
      photo.value = microsoftPhoto.value;
      photoChangedLocally.value = false;
      cachePhotoOnly(microsoftPhoto.value);
    }
  }

  /**
   * Limpa todos os dados em cache do Graph, incluindo perfil e fotos.
   *
   * @example
   * ```typescript
   * const { clearCache } = useGraph();
   * clearCache();
   * ```
   */
  function clearCache(): void {
    clearGraphCache();
    profile.value = null;
    photo.value = null;
    microsoftPhoto.value = null;
    photoChangedLocally.value = false;
  }

  return {
    profile: readonly(profile),
    photo: readonly(photo),
    microsoftPhoto: readonly(microsoftPhoto),
    photoChangedLocally: readonly(photoChangedLocally),
    isLoading: readonly(isLoading),
    graphError: readonly(graphError),
    graphAuthExpired: readonly(graphAuthExpired),
    loadProfile,
    uploadLocalPhoto,
    restoreMicrosoftPhoto,
    clearCache,
  };
}

export interface GraphSyncResult {
  success: boolean;
  authExpired: boolean;
}

/**
 * Busca o perfil mais recente do Microsoft Graph e atualiza o snapshot do
 * usuário, preservando os overrides editáveis. Usado para manter as assinaturas
 * atualizadas sempre que um painel é aberto, sem depender do estado reativo
 * de {@link useGraph}.
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @returns Sucesso da sincronização e se a sessão expirou.
 *
 * @example
 * ```typescript
 * const { success, authExpired } = await syncUserDataFromGraph(getMsalInstance());
 * ```
 */
export async function syncUserDataFromGraph(
  msalInstance: msal.PublicClientApplication
): Promise<GraphSyncResult> {
  try {
    const { profile } = await loadProfileWithCache(msalInstance, true);
    userDataCache.refreshFromGraph(mapGraphProfileToUserData(profile));

    await flushRoaming();

    return { success: true, authExpired: false };
  } catch (err: any) {
    return { success: false, authExpired: err instanceof AuthError && err.interactionRequired };
  }
}
